import { all, select, takeEvery, fork, call, put } from 'redux-saga/effects'

import { logDashboardActions } from '@redux/store/index'

import { logApi, ResType, ParameterType } from '@api'
import { Logger } from '@utils'

import type { LogDashboardStateType } from '@redux/types'

// API wrappers
const apiGetLogSummary = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await logApi.getLogSummary(data)
	return res
}

const apiGetLogList = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await logApi.getLogList(data)
	return res
}

const apiGetLogTrace = async (data: any): Promise<ResType | undefined> => {
	const res: ResType | undefined = await logApi.getLogTrace(data)
	return res
}

const apiGetLogSetting = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await logApi.getLogSetting()
	return res
}

const apiUpdateLogSetting = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await logApi.updateLogSetting(data)
	return res
}

// Sagas
function* getLogSummarySaga() {
	try {
		yield put(logDashboardActions.SUMMARY_STATUS('loading'))
		const date: string = yield select((state: any) => state.logDashboard.summaryDate)

		const apiRes: ResType | undefined = yield call(apiGetLogSummary, {
			query: { date },
		})

		if (apiRes !== undefined && apiRes.status === 200) {
			yield put(logDashboardActions.SUMMARY(apiRes.data[0] as any))
			yield put(logDashboardActions.SUMMARY_STATUS('ok'))
		} else {
			yield put(logDashboardActions.SUMMARY_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(logDashboardActions.SUMMARY_STATUS('empty'))
	}
}

function* getLogListSaga() {
	try {
		yield put(logDashboardActions.LOGS_STATUS('loading'))
		const state: LogDashboardStateType = yield select((s: any) => s.logDashboard)

		const query: Record<string, any> = {
			date: state.listDate,
			page: state.listPage,
			limit: state.listLimit,
		}
		if (state.listLevel) query.level = state.listLevel
		if (state.listCountry) query.country = state.listCountry
		if (state.listMethod) query.method = state.listMethod
		if (state.listUrl) query.url = state.listUrl

		const apiRes: ResType | undefined = yield call(apiGetLogList, { query })

		if (apiRes !== undefined && apiRes.status === 200) {
			const data = apiRes.data[0] as any
			yield put(logDashboardActions.LOGS(data.logs))
			yield put(logDashboardActions.LOGS_TOTAL_COUNT(data.totalCount))
			yield put(logDashboardActions.LOGS_STATUS(data.logs.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(logDashboardActions.LOGS_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(logDashboardActions.LOGS_STATUS('empty'))
	}
}

function* getLogTraceSaga() {
	try {
		yield put(logDashboardActions.TRACES_STATUS('loading'))
		const requestId: string = yield select((s: any) => s.logDashboard.traceRequestId)
		const date: string = yield select((s: any) => s.logDashboard.listDate)

		const apiRes: ResType | undefined = yield call(apiGetLogTrace, {
			params: { requestId },
			query: { date },
		})

		if (apiRes !== undefined && apiRes.status === 200) {
			yield put(logDashboardActions.TRACES((apiRes.data[0] as any).traces))
			yield put(logDashboardActions.TRACES_STATUS('ok'))
			yield put(logDashboardActions.TRACE_MODAL_OPEN(true))
		} else {
			yield put(logDashboardActions.TRACES_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(logDashboardActions.TRACES_STATUS('empty'))
	}
}

function* getLogSettingSaga() {
	try {
		yield put(logDashboardActions.LOG_SETTING_STATUS('loading'))

		const apiRes: ResType | undefined = yield call(apiGetLogSetting)

		if (apiRes !== undefined && apiRes.status === 200) {
			yield put(logDashboardActions.LOG_SETTING(apiRes.data[0] as any))
			yield put(logDashboardActions.LOG_SETTING_STATUS('ok'))
		} else {
			yield put(logDashboardActions.LOG_SETTING_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(logDashboardActions.LOG_SETTING_STATUS('empty'))
	}
}

function* updateLogSettingSaga() {
	try {
		const logSetting: LogDashboardStateType['logSetting'] = yield select(
			(s: any) => s.logDashboard.logSetting,
		)

		if (!logSetting) return

		const apiRes: ResType | undefined = yield call(apiUpdateLogSetting, {
			body: { recipients: logSetting.recipients },
		})

		if (apiRes !== undefined && apiRes.status === 200) {
			yield put(logDashboardActions.LOG_SETTING(apiRes.data[0] as any))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(logDashboardActions.TRIGGER_GET_SUMMARY, getLogSummarySaga)
	yield takeEvery(logDashboardActions.TRIGGER_GET_LOG_LIST, getLogListSaga)
	yield takeEvery(logDashboardActions.TRIGGER_GET_TRACE, getLogTraceSaga)
	yield takeEvery(logDashboardActions.TRIGGER_GET_LOG_SETTING, getLogSettingSaga)
	yield takeEvery(logDashboardActions.TRIGGER_UPDATE_LOG_SETTING, updateLogSettingSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
